import { useState } from 'react';
import {
  MAX_SCHEDULE_AHEAD_MS,
  MIN_SCHEDULE_LEAD_MS,
  type ReminderRepeatKind,
} from '@avslack/shared';
import { useSession } from '../lib/session';
import { Dialog, useAnimatedClose } from './Dialog';
import { RepeatPicker, repeatRuleOf } from './RepeatPicker';

/** Az ismetlodes, ahogy a szerver keri (`scheduleMessageInput.repeat`). */
export interface ScheduleRepeat {
  kind: ReminderRepeatKind;
  days: number[];
  until: string | null;
}

/**
 * Egyedi idopont az idozitett uzenethez.
 *
 * A menu nehany kesz ajanlatot kinal (10 perc, holnap reggel, hetfo reggel) -
 * azok a gyakori esetek. Ez a parbeszed a tobbire valo: konkret datum es ora.
 *
 * A szerver eddig is barmilyen idopontot elfogadott; csak a valaszto hianyzott.
 */

/** A `datetime-local` mezo alakja: `2026-09-04T14:30`, HELYI idoben. */
function toInputValue(date: Date): string {
  const pad = (value: number) => String(value).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

/** Alapertelmezes: a kovetkezo egesz ora. Kerek szam, es biztosan a jovoben van. */
function defaultWhen(): Date {
  const date = new Date();
  date.setHours(date.getHours() + 1, 0, 0, 0);
  return date;
}

export function ScheduleDialog({
  onCancel: cancelNow,
  onPick,
  repeating = false,
  title,
  description,
  confirmLabel = 'Időzítés',
}: {
  onCancel: () => void;
  /** Mas celra (pl. emlekezteto mentett elemre): sajat feliratok. */
  title?: string;
  description?: string;
  confirmLabel?: string;
  onPick: (sendAt: Date, repeat?: ScheduleRepeat) => void;
  /**
   * ISMETLODO uzenet (versenyaudit U9): a kezdes mellett a ritmus is
   * megadhato. Alapbol "hetente", mert a heti bejelentes a leggyakoribb.
   */
  repeating?: boolean;
}) {
  const onCancel = useAnimatedClose(cancelNow);
  const session = useSession();
  const [value, setValue] = useState(() => toInputValue(defaultWhen()));

  const when = new Date(value);
  const valid = !Number.isNaN(when.getTime());

  const [repeatKind, setRepeatKind] = useState<ReminderRepeatKind>(repeating ? 'weekly' : 'none');
  const [repeatDays, setRepeatDays] = useState<Set<number>>(() => new Set());
  const [repeatUntil, setRepeatUntil] = useState('');
  const rule = repeatRuleOf({
    kind: repeatKind,
    days: repeatDays,
    until: repeatUntil,
    start: valid ? when : null,
    timeZone: session.data?.user?.timezone || 'Europe/Budapest',
  });
  const toggleDay = (day: number) =>
    setRepeatDays((current) => {
      const next = new Set(current);
      if (next.has(day)) next.delete(day);
      else next.add(day);
      return next;
    });
  const ahead = valid ? when.getTime() - Date.now() : 0;

  /*
    A hibauzenet MEGMONDJA a hatart, nem csak azt, hogy "ervenytelen".

    A ket korlat a szerverrol jon (`MIN_SCHEDULE_LEAD_MS`,
    `MAX_SCHEDULE_AHEAD_MS`), tehat itt nem talalgatunk: ugyanaz a szabaly
    ervenyes, mint amit a szerver is ellenoriz.
  */
  const problem = !valid
    ? 'Ez az időpont nem értelmezhető.'
    : ahead < MIN_SCHEDULE_LEAD_MS
      ? 'Legalább egy perccel későbbre időzíts – különben nincs értelme.'
      : ahead > MAX_SCHEDULE_AHEAD_MS
        ? `Legfeljebb ${Math.round(MAX_SCHEDULE_AHEAD_MS / 86_400_000)} nappal előre lehet időzíteni.`
        : rule.until && new Date(rule.until).getTime() < when.getTime()
          ? 'Az ismétlés vége nem lehet a kezdés előtt.'
          : null;

  return (
    <Dialog
      open
      onOpenChange={(next) => !next && onCancel()}
      title={title ?? (repeating ? 'Ismétlődő üzenet' : 'Egyedi időpont')}
      description={
        description ??
        (repeating
          ? 'Mikor menjen el először, és milyen gyakran ismétlődjön?'
          : 'Mikor menjen el az üzenet?')
      }
    >
      <label className="block">
        <span className="text-2xs font-semibold uppercase tracking-wide text-ink-faint">
          {repeating ? 'Első küldés' : 'Dátum és idő'}
        </span>
        <input
          type="datetime-local"
          value={value}
          min={toInputValue(new Date(Date.now() + MIN_SCHEDULE_LEAD_MS))}
          max={toInputValue(new Date(Date.now() + MAX_SCHEDULE_AHEAD_MS))}
          onChange={(event) => setValue(event.target.value)}
          className="mt-1.5 w-full rounded-control border border-border bg-surface-sunken px-3 py-2 text-sm"
        />
      </label>

      {/*
        Visszaolvassuk, MIKOR is lesz ez.

        A `datetime-local` szuken formaz, es egy elgepelt honap konnyen
        eszrevetlen marad. A teljes, magyar alaku datum kimondja, mire
        szamitson a felhasznalo.
      */}
      {valid && !problem && (
        <p className="mt-2 text-xs text-ink-muted">
          {when.toLocaleString('hu-HU', {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
            weekday: 'long',
            hour: '2-digit',
            minute: '2-digit',
          })}
        </p>
      )}

      {repeating && (
        <RepeatPicker
          kind={repeatKind}
          onKind={setRepeatKind}
          days={repeatDays}
          onToggleDay={toggleDay}
          until={repeatUntil}
          onUntil={setRepeatUntil}
          start={valid ? when : null}
          rule={rule}
        />
      )}

      {problem && (
        <p role="alert" className="mt-2 text-xs text-danger">
          {problem}
        </p>
      )}

      <div className="mt-5 flex justify-end gap-2">
        <button
          type="button"
          onClick={onCancel}
          className="rounded-control px-3 py-2 text-sm text-ink-muted hover:text-ink"
        >
          Mégse
        </button>
        <button
          type="button"
          onClick={() =>
            onPick(
              when,
              repeatKind === 'none'
                ? undefined
                : { kind: repeatKind, days: rule.days, until: rule.until },
            )
          }
          disabled={Boolean(problem)}
          className="rounded-control bg-accent px-4 py-2 text-sm font-semibold text-accent-ink disabled:opacity-50"
        >
          {confirmLabel}
        </button>
      </div>
    </Dialog>
  );
}
